import React, { useState, useEffect, useMemo } from 'react';
import Sidebar from './sidebar';
import WaybillDetailsModal from './modal_windows/WaybillDetailsModal';

import DoneAllIcon from '@mui/icons-material/DoneAll';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import {
  StatCard, RefreshButton, SearchField, FilterPill,
  StatusBadge, TableCard, LoadingState, EmptyState, Pagination,
} from '../../ui/CrmPage';

const PAGE_SIZE = 15;
// Идентификатор статуса «Завершен» в таблице waybillstatus
const WAYBILL_DONE_STATUS_ID = 4;
const isClosed = (name) => /завершен|закрыт/i.test(String(name || ''));

const Waybill = () => {
  const [orders, setOrders] = useState([]);
  const [orderDetails, setOrderDetails] = useState({});
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('Все');
  const [currentPage, setCurrentPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedOrderDetails, setSelectedOrderDetails] = useState(null);

  useEffect(() => {
    setLoading(true);
    fetch(`${process.env.REACT_APP_API_URL}v1/waybill_view`)
      .then(response => response.json())
      .then(data => {
        if (Array.isArray(data)) {
          setOrders(data);
          data.forEach(order => fetchOrderDetails(order.id));
        }
      })
      .catch(error => console.error('Ошибка:', error))
      .finally(() => setLoading(false));
  }, [reloadKey]);

  const fetchOrderDetails = (orderId) => {
    fetch(`${process.env.REACT_APP_API_URL}v1/waybill_view/${orderId}`)
      .then(response => response.json())
      .then(data => setOrderDetails(prev => ({ ...prev, [orderId]: data })))
      .catch(error => console.error('Ошибка:', error));
  };

  const handleOrderClick = (orderId) => {
    setSelectedOrderDetails(orderDetails[orderId]);
    setIsModalOpen(true);
  };

  const completeWaybill = async (waybill) => {
    if (!window.confirm(`Завершить путевой №${waybill.id}?`)) return;
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}v1/waybills/${waybill.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_waybillstatus: WAYBILL_DONE_STATUS_ID,
          id_vehicles: waybill.id_vehicles ?? null,
          id_regions: waybill.id_regions ?? null,
          id_drivers: waybill.id_drivers ?? null,
          id_delivery_areas: waybill.id_delivery_areas ?? null,
        }),
      });
      if (!res.ok) throw new Error(await res.text());
      setReloadKey((k) => k + 1);
    } catch (e) {
      alert(`Не удалось завершить: ${e.message}`);
    }
  };

  const deleteWaybill = async (waybillId) => {
    if (!window.confirm(`Удалить путевой №${waybillId}?`)) return;
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}v1/waybills/${waybillId}`, {
        method: 'DELETE',
      });
      if (!res.ok) throw new Error(await res.text());
      setReloadKey((k) => k + 1);
    } catch (e) {
      alert(`Не удалось удалить: ${e.message}`);
    }
  };

  const statusGroups = useMemo(() =>
    Array.from(new Set(orders.map(o => o.waybill_status_name).filter(Boolean))), [orders]);

  const summary = useMemo(() => ({
    total: orders.length,
    created: orders.filter(o => /создан|формир/i.test(o.waybill_status_name || '')).length,
    transit: orders.filter(o => /в пути|выдан/i.test(o.waybill_status_name || '')).length,
    closed: orders.filter(o => /закрыт|завершен/i.test(o.waybill_status_name || '')).length,
  }), [orders]);

  const filteredOrders = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return orders.filter(o => {
      if (statusFilter !== 'Все' && o.waybill_status_name !== statusFilter) return false;
      if (!term) return true;
      return (
        String(o.id || '').includes(term) ||
        (o.full_name || '').toLowerCase().includes(term) ||
        (o.vname || '').toLowerCase().includes(term) ||
        (o.region || '').toLowerCase().includes(term)
      );
    });
  }, [orders, searchTerm, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredOrders.length / PAGE_SIZE));
  const pageStart = (currentPage - 1) * PAGE_SIZE;
  const paginatedOrders = filteredOrders.slice(pageStart, pageStart + PAGE_SIZE);

  const resetFilters = () => { setSearchTerm(''); setStatusFilter('Все'); setCurrentPage(1); };
  const hasFilters = searchTerm || statusFilter !== 'Все';

  return (
    <div className="waybill-container crmpage">
      <div className="waybill-sidebar">
        <Sidebar />
      </div>

      <div className="waybill-basic-container crmpage__main">
        <header className="crmpage__head">
          <h1 className="crmpage__title">Путевые листы</h1>
        </header>

        <section className="crmpage__stats">
          <StatCard label="Всего"      value={summary.total}   accent="neutral" />
          <StatCard label="Сформированы" value={summary.created} accent="warning" />
          <StatCard label="В пути"     value={summary.transit} accent="info" />
          <StatCard label="Закрыты"    value={summary.closed}  accent="success" />
          <RefreshButton onClick={() => setReloadKey(k => k + 1)} />
        </section>

        <section className="crmpage__filters">
          <SearchField
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
            placeholder="Поиск: номер, экспедитор, транспорт…"
          />
          <div className="crmpage__pills">
            <FilterPill active={statusFilter === 'Все'}
              onClick={() => { setStatusFilter('Все'); setCurrentPage(1); }}
              count={orders.length}>Все</FilterPill>
            {statusGroups.map(s => (
              <FilterPill key={s}
                active={statusFilter === s}
                onClick={() => { setStatusFilter(s); setCurrentPage(1); }}
                count={orders.filter(o => o.waybill_status_name === s).length}>{s}</FilterPill>
            ))}
          </div>
          {hasFilters && (
            <button type="button" className="crmpage__reset" onClick={resetFilters}>Сбросить</button>
          )}
        </section>

        <TableCard>
          {loading ? (
            <LoadingState text="Загружаем путевые листы…" />
          ) : paginatedOrders.length === 0 ? (
            <EmptyState title="Путевых нет" text={hasFilters ? 'Сбросьте фильтры.' : 'Путевые листы появятся здесь.'} />
          ) : (
            <>
              <div className="crmpage__table-wrap">
                <table className="crmpage__table">
                  <colgroup>
                    <col style={{ width: 80 }} />
                    <col />
                    <col />
                    <col style={{ width: 160 }} />
                    <col style={{ width: 140 }} />
                    <col style={{ width: 200 }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>Номер</th>
                      <th>Экспедитор</th>
                      <th className="crmpage__hide-880">Транспорт</th>
                      <th>Статус</th>
                      <th className="crmpage__hide-1000">Регион</th>
                      <th style={{ textAlign: 'right' }}>Действия</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedOrders.map((order, idx) => {
                      const closed = isClosed(order.waybill_status_name);
                      return (
                        <tr key={idx} className="crmpage__row" onClick={() => handleOrderClick(order.id)}>
                          <td><span className="crmpage__num">№{order.id}</span></td>
                          <td>
                            <div className="crmpage__primary">{order.full_name || '—'}</div>
                          </td>
                          <td className="crmpage__muted crmpage__hide-880">
                            {order.vname} {order.registration_number ? `(${order.registration_number})` : ''}
                          </td>
                          <td><StatusBadge name={order.waybill_status_name} /></td>
                          <td className="crmpage__muted crmpage__hide-1000">{order.region || '—'}</td>
                          <td style={{ textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
                            <div style={{ display: 'inline-flex', gap: 6, justifyContent: 'flex-end' }}>
                              {!closed && (
                                <button
                                  type="button"
                                  className="crmpage__btn crmpage__btn--ghost"
                                  style={{ height: 28, fontSize: 12, color: '#0F5B3C', background: 'rgba(74,165,135,0.10)', border: 'none' }}
                                  onClick={() => completeWaybill(order)}
                                >
                                  <DoneAllIcon sx={{ fontSize: 16 }} /> Завершить
                                </button>
                              )}
                              <button
                                type="button"
                                className="crmpage__btn crmpage__btn--ghost"
                                style={{ height: 28, fontSize: 12, color: '#B53A36', background: 'rgba(235,87,87,0.10)', border: 'none' }}
                                onClick={() => deleteWaybill(order.id)}
                                title="Удалить путевой"
                              >
                                <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="crmpage__cards">
                {paginatedOrders.map((o, idx) => (
                  <div key={idx} className="crmpage__card" onClick={() => handleOrderClick(o.id)}>
                    <div className="crmpage__card-head">
                      <span className="crmpage__num">№{o.id}</span>
                      <StatusBadge name={o.waybill_status_name} />
                    </div>
                    <div className="crmpage__card-row">
                      <span className="crmpage__card-label">Экспедитор</span>
                      <span className="crmpage__card-val">{o.full_name || '—'}</span>
                    </div>
                    <div className="crmpage__card-row">
                      <span className="crmpage__card-label">Транспорт</span>
                      <span className="crmpage__card-val">{o.vname} {o.registration_number ? `(${o.registration_number})` : ''}</span>
                    </div>
                  </div>
                ))}
              </div>

              <Pagination current={currentPage} total={totalPages} onChange={setCurrentPage} />
            </>
          )}
        </TableCard>
      </div>

      {selectedOrderDetails && (
        <WaybillDetailsModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          orderDetails={selectedOrderDetails}
        />
      )}
    </div>
  );
};

export default Waybill;
